import { Component, OnDestroy, OnInit } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppState } from '@fcom/core';
import { ButtonPosition, IconPosition, ModalButtons } from '@fcom/ui-components';
import { ElementTypes } from '@fcom/common';
import { unsubscribe } from '@fcom/core/utils';

import { Passenger } from '../../api/models';
import { UpgradePaymentService } from '../../services/upgrade-payment.service';
import { State } from '../../interfaces/store.interface';
import { FlightBasket } from '../../interfaces/upgrades.interface';
import {
  getBasketFlights,
  getStage,
  getBasketPassengerIds,
  getPassengers,
  getChildIsTravellingAlone,
} from '../../store/selectors';
import { ModalActions, StatusActions, UpgradeActions } from '../../store/actions';
import { Stage } from '../../interfaces/stage.interface';
import { PaymentType } from '../../interfaces';
import { UpgradeAnalyticsService } from '../../services/upgrade-analytics.service';

@Component({
  selector: 'fin-upgrade-continue-footer',
  templateUrl: './continue-footer.component.html',
  styleUrls: ['continue-footer.component.scss'],
})
export class ContinueFooterComponent implements OnDestroy, OnInit {
  readonly Stage = Stage;
  readonly ButtonPosition = ButtonPosition;
  readonly ElementTypes = ElementTypes;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  flightsBasket$: Observable<FlightBasket[]>;
  passengers$: Observable<Passenger[]>;
  selectedPassengers$: Observable<string[]>;
  openPnrModal: boolean;
  stage: Stage;
  shouldSplitPnr: boolean;
  currentStage$: Observable<Stage>;
  selectedPayments$: Observable<string[]>;
  childIsTravellingAlone$: Observable<boolean>;
  passengerStageCompleted$: Observable<boolean>;
  loading = false;
  subscriptions: Subscription = new Subscription();
  continueButtonI18n$: Observable<string>;

  constructor(
    private store: Store<State>,
    private upgradePaymentService: UpgradePaymentService,
    private store$: Store<AppState>,
    private upgradeAnalyticsService: UpgradeAnalyticsService
  ) {}

  ngOnInit(): void {
    this.selectedPayments$ = this.upgradePaymentService.getAllSelectedPayments();
    this.flightsBasket$ = this.store.pipe(select(getBasketFlights));
    this.passengers$ = this.store.pipe(select(getPassengers));
    this.selectedPassengers$ = this.store.pipe(select(getBasketPassengerIds));
    this.currentStage$ = this.store.pipe(select(getStage));
    this.childIsTravellingAlone$ = this.store.pipe(select(getChildIsTravellingAlone));
    this.passengerStageCompleted$ = combineLatest([this.selectedPassengers$, this.childIsTravellingAlone$]).pipe(
      map(([selectedPassengers, childIsTravellingAlone]) => selectedPassengers.length > 0 && !childIsTravellingAlone)
    );

    this.subscriptions.add(
      this.currentStage$.subscribe((stage: Stage) => {
        this.stage = stage;
      })
    );

    this.subscriptions.add(
      combineLatest([this.passengers$, this.selectedPassengers$]).subscribe(([passengers, selected]) => {
        this.shouldSplitPnr = passengers.length !== selected.length;
      })
    );

    this.continueButtonI18n$ = combineLatest([this.currentStage$, this.flightsBasket$]).pipe(
      map(([stage, flightsBasket]) => {
        const hasMoneyPayment = flightsBasket.some((flightBasket) => flightBasket.paymentType === PaymentType.money);

        if (stage === Stage.REVIEW) {
          return hasMoneyPayment ? 'purchase.review.proceed' : 'oneUpgrade.requestUpgrade';
        }

        return 'actions.next';
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  onContinue(event: Event): void {
    if (this.stage === Stage.REVIEW) {
      this.openReviewDisclaimerModal(event);
      return;
    }

    if (this.shouldSplitPnr && this.stage === Stage.PASSENGERS) {
      this.openPnrSplitModal(event);
    } else {
      this.moveToNextStage();
    }
  }

  moveToNextStage(): void {
    const stageMap = {
      [Stage.PASSENGERS]: Stage.FLIGHTS,
      [Stage.FLIGHTS]: Stage.REVIEW,
      [Stage.REVIEW]: Stage.SUCCESS,
    };
    this.store.dispatch(StatusActions.setStage({ stage: stageMap[this.stage] }));
  }

  openPnrSplitModal(event: Event): void {
    event.stopPropagation();

    this.store$.dispatch(
      ModalActions.openModal({
        modal: {
          title: 'modal.genericTitle',
          fragment: 'fragments.oneUpgrade.pnrSplit.url',
          buttons: ModalButtons.CONTINUE_CANCEL,
          handlers: {
            confirm: () => this.moveToNextStage(),
          },
        },
        returnTarget: '.btn-cta',
      })
    );
  }

  openReviewDisclaimerModal(event: Event): void {
    event.stopPropagation();

    this.store$.dispatch(
      ModalActions.openModal({
        modal: {
          title: 'modal.genericTitle',
          fragment: 'fragments.oneUpgrade.reviewDisclamer.url',
          buttons: ModalButtons.CONTINUE_CANCEL,
          handlers: {
            confirm: () => this.confirmUpgrade(),
          },
        },
        returnTarget: '.btn-cta',
      })
    );
  }

  confirmUpgrade(): void {
    this.loading = true;
    this.upgradeAnalyticsService.sendInitiateCheckoutEvent();
    this.store.dispatch(UpgradeActions.upgradeFlights());
  }
}
